import { useContext } from 'react';

import { Tag } from '@gympass/yoga';
import { CheckedFull, CheckedHalf, AlertTriangle } from '@gympass/yoga-icons';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationContext } from 'context';
import { useLogger } from 'hooks/useLogger/useLogger';
import { getInvoiceStatus } from 'services';

import { InvoiceStatusProps } from './types';

interface MonthStatusMap {
  [key: string]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
    color: string;
  };
}

const InvoiceStatus: React.FC<InvoiceStatusProps> = ({
  year,
  month,
  onChangeStatus,
}) => {
  const intl = useIntl();
  const logger = useLogger();
  const { setNotification } = useContext(NotificationContext);

  const monthStatusMap: MonthStatusMap = {
    open: {
      icon: CheckedFull,
      color: 'success',
    },
    closed: {
      icon: AlertTriangle,
      color: 'neutral',
    },
    pending: {
      icon: CheckedHalf,
      color: 'attention',
    },
  };

  const {
    data: invoiceStatus,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [year, month, 'invoiceStatus'],
    enabled: !!(year && month),
    queryFn: () => getInvoiceStatus(Number(year), Number(month)),
    onSuccess: onChangeStatus,
    onError: () => {
      logger.error('[Invoice][getInvoiceStatus]');
      setNotification({
        message: intl.formatMessage({
          id: 'CPPLATFORM.FEEDBACKGENERICERROR.TRYAGAINLATER',
        }),
        type: 'failure',
      });
    },
  });

  const monthStatus = monthStatusMap[invoiceStatus || 'closed'];

  return isFetching || isError ? (
    <></>
  ) : (
    <Tag.Informative
      mr="small"
      icon={monthStatus?.icon}
      variant={monthStatus?.color}
      data-testid="invoice-header-month-status"
    >
      <FormattedMessage
        id={`CPPLATFORM.LABEL.${invoiceStatus?.toUpperCase()}`}
      />
    </Tag.Informative>
  );
};

export default InvoiceStatus;
